import { useEffect, useState } from 'react';
import { ExternalLink, MapPin, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { getLocationStatusSummary } from './locationStatus';

const severityStyles = {
  critical: 'text-red-300 bg-red-500/10 border-red-500/20',
  warning: 'text-amber-200 bg-amber-500/10 border-amber-500/20',
  healthy: 'text-emerald-200 bg-emerald-500/10 border-emerald-500/20'
};

const severityLabels = {
  critical: 'Critical',
  warning: 'Needs Attention',
  healthy: 'Healthy'
};

const pluralize = (count, singular, plural) => `${count} ${count === 1 ? singular : plural}`;

const actionLinkClass = 'flex items-center justify-center rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-xs font-semibold text-gray-200 transition-colors hover:bg-white/10 hover:text-white';
const disabledActionClass = 'cursor-not-allowed rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2 text-xs font-semibold text-gray-600';

const launchpadGlassCardStyle = {
  backgroundColor: 'rgba(255, 255, 255, 0.03)',
  backdropFilter: 'blur(64px) saturate(200%)',
  WebkitBackdropFilter: 'blur(64px) saturate(200%)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.16), inset 0 -1px 0 rgba(255, 255, 255, 0.04), 0 30px 86px rgba(0, 0, 0, 0.44)',
};

const buildGoogleMapsUrl = (location = {}) => {
  const hasCoordinates = Number.isFinite(Number(location.lat)) && Number.isFinite(Number(location.lng));
  const query = location.address || (hasCoordinates ? `${location.lat},${location.lng}` : location.boardName);

  if (!query) return null;

  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
};

export default function LocationPreviewPanel({ location, issues = [], ticketCount = null, onClose }) {
  const authenticatedFetch = useAuthenticatedFetch();
  const [photoState, setPhotoState] = useState({ status: 'empty', url: null });

  useEffect(() => {
    let isMounted = true;
    let objectUrl = null;

    if (!location?.primaryPhotoAssetId) {
      setPhotoState({ status: 'empty', url: null });
      return undefined;
    }

    setPhotoState({ status: 'loading', url: null });

    const loadPhoto = async () => {
      try {
        const response = await authenticatedFetch(`/api/locations/assets/${location.primaryPhotoAssetId}`);

        if (!response.ok) {
          throw new Error('Location photo request failed.');
        }

        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);

        if (isMounted) {
          setPhotoState({ status: 'loaded', url: objectUrl });
        } else {
          URL.revokeObjectURL(objectUrl);
        }
      } catch (error) {
        if (isMounted) {
          setPhotoState({ status: 'failed', url: null });
        }
      }
    };

    loadPhoto();

    return () => {
      isMounted = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [authenticatedFetch, location?.primaryPhotoAssetId]);

  if (!location) return null;

  const statusSummary = getLocationStatusSummary(issues);
  const hasLocationId = location.locationId !== null && location.locationId !== undefined && location.locationId !== '';
  const hasOperationsItemId = Boolean(location.operationsMondayItemId);
  const hasTicketCount = Number.isFinite(ticketCount);
  const showPhoto = photoState.status === 'loaded' && Boolean(photoState.url);
  const googleMapsUrl = buildGoogleMapsUrl(location);

  return (
    <aside className="absolute left-4 bottom-4 z-[450] w-[min(22rem,calc(100%-2rem))] overflow-hidden rounded-2xl text-white" style={launchpadGlassCardStyle}>
      <div className="relative h-36 border-b border-white/10 bg-black">
        {showPhoto ? (
          <img
            src={photoState.url}
            alt={`${location.boardName} location`}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-2 bg-[#111] text-gray-500">
            <MapPin className="h-8 w-8 text-gray-600" aria-hidden="true" />
            <span className="text-xs font-semibold uppercase tracking-widest">No location photo</span>
          </div>
        )}
        <button
          type="button"
          aria-label="Close location preview"
          onClick={onClose}
          className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 bg-black/70 text-gray-300 backdrop-blur transition-colors hover:bg-white/10 hover:text-white"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      <div className="space-y-4 p-4">
        <div>
          <div className="mb-2 flex items-start justify-between gap-3">
            <h2 className="text-base font-semibold leading-snug text-white">{location.boardName}</h2>
            <span className={`shrink-0 rounded-full border px-2 py-1 text-[10px] font-bold uppercase tracking-wider ${severityStyles[statusSummary.highestSeverity]}`}>
              {severityLabels[statusSummary.highestSeverity]}
            </span>
          </div>
          {location.address && (
            <a
              href={googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              title="Open address in Google Maps"
              className="flex items-center gap-2 text-xs leading-5 text-gray-400 transition-colors hover:text-white"
            >
              <MapPin className="h-3.5 w-3.5 shrink-0 text-gray-500" aria-hidden="true" />
              {location.address}
              <ExternalLink className="h-3 w-3 shrink-0 text-gray-600" aria-hidden="true" />
            </a>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2">
            <div className="text-sm font-semibold text-white">
              {pluralize(statusSummary.issueCount, 'active issue', 'active issues')}
            </div>
            <div className="mt-1 text-[10px] uppercase tracking-widest text-gray-500">Monday status</div>
          </div>
          <div className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2">
            <div className="text-sm font-semibold text-white">
              {hasTicketCount
                ? pluralize(ticketCount, 'open ticket', 'open tickets')
                : 'Ticket data unavailable'}
            </div>
            <div className="mt-1 text-[10px] uppercase tracking-widest text-gray-500">Support queue</div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2">
          {hasLocationId ? (
            <Link
              to={`/locations/${location.locationId}`}
              className={actionLinkClass}
            >
              Open Location
            </Link>
          ) : (
            <button
              type="button"
              disabled
              className={disabledActionClass}
            >
              Open Location
            </button>
          )}
          {hasOperationsItemId ? (
            <Link
              to={`/admin?locationAccountId=${encodeURIComponent(location.operationsMondayItemId)}`}
              className={actionLinkClass}
            >
              Operations
            </Link>
          ) : (
            <button
              type="button"
              disabled
              className={disabledActionClass}
            >
              Operations
            </button>
          )}
          {hasLocationId ? (
            <Link
              to={`/support?locationId=${location.locationId}`}
              className={actionLinkClass}
            >
              Support
            </Link>
          ) : (
            <button
              type="button"
              disabled
              className={disabledActionClass}
            >
              Support
            </button>
          )}
        </div>
      </div>
    </aside>
  );
}
